import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CORE_FEATURE_TOGGLE, InteractionService } from '@core';
import { merge, of } from 'rxjs';
import { distinctUntilChanged, filter, map, skip } from 'rxjs/operators';
import { NavigationService } from './navigation';
import { PageService } from './page';
import { idleTransitionNavigation, isMainHomeUrl, resolveIdleTapPageId } from './idle-navigation-policy';

/** Main-page lifecycle must remain active even when the tab bar has no rendered UI. */
@Injectable()
export class IdleNavigationService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly interaction = inject(InteractionService);
  private readonly translate = inject(TranslateService);
  private readonly featureToggle = inject(CORE_FEATURE_TOGGLE);
  private readonly navigation = toSignal(inject(NavigationService).config$);
  private readonly pages = toSignal(inject(PageService).config$, { initialValue: [] });
  private readonly attemptedWake = signal(false);
  private readonly navigationFailed = signal(false);

  public readonly currentUrl = toSignal(merge(
    of(this.router.url),
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
    ),
  ), { initialValue: this.router.url });
  public readonly tabBarEnabled = computed(() => this.navigation()?.tabBar.enabled !== false);
  public readonly showWakeSurface = computed(() => !this.tabBarEnabled() && isMainHomeUrl(this.currentUrl()));
  public readonly waking = signal(false);
  private readonly targetPageId = computed(() => resolveIdleTapPageId(this.navigation(), this.pages(), this.currentUrl()));
  public readonly wakeUnavailable = computed(() => this.attemptedWake() && (!this.targetPageId() || this.navigationFailed()));

  constructor() {
    this.interaction.idle$.pipe(
      distinctUntilChanged(), skip(1), takeUntilDestroyed(this.destroyRef),
    ).subscribe(idle => {
      const destination = idleTransitionNavigation(idle, this.tabBarEnabled(), this.currentUrl());
      if (idle) {
        this.attemptedWake.set(false);
        this.navigationFailed.set(false);
        this.resetLanguage();
      }
      if (destination === 'home') {
        void this.router.navigate(['/', 'main', 'home'], { replaceUrl: !this.tabBarEnabled() });
      } else if (destination === 'legacy-wake') {
        void this.router.navigate(this.featureToggle.idleClickGoTo);
      }
    });
  }

  public async openFromHome(): Promise<void> {
    if (!this.showWakeSurface() || this.waking()) return;
    this.attemptedWake.set(true);
    const pageId = this.targetPageId();
    if (!pageId) return;
    this.waking.set(true);
    this.navigationFailed.set(false);
    this.interaction.interact();
    try {
      const opened = await this.router.navigate(['/', 'main', 'page', pageId], { queryParams: { ref: 'idle' }, replaceUrl: true });
      this.navigationFailed.set(!opened || isMainHomeUrl(this.currentUrl()));
    } catch {
      this.navigationFailed.set(true);
    } finally {
      this.waking.set(false);
    }
  }

  public returnToIdle(): void {
    if (this.interaction.idling) {
      this.resetLanguage();
      void this.router.navigate(['/', 'main', 'home'], { replaceUrl: true });
    } else {
      // The synchronous idle subscription above owns the single return navigation.
      this.interaction.idle();
    }
  }

  private resetLanguage(): void {
    const fallback = this.translate.getFallbackLang();
    if (fallback && this.translate.getCurrentLang() !== fallback) this.translate.use(fallback);
  }
}
